import dayjs from 'dayjs'

export function getCurrentQuarterRange(): [string, string] {
  const now = dayjs()
  const currentMonth = now.month()
  const startMonth = Math.floor(currentMonth / 3) * 3
  const start = now.month(startMonth).startOf('month')
  const end = now.month(startMonth + 2).endOf('month')
  return [start.format('YYYY-MM-DD'), end.format('YYYY-MM-DD')]
}

export function getQuarterRangeForYear(year: number, quarter: 1 | 2 | 3 | 4) {
  const startMonth = (quarter - 1) * 3
  return [
    dayjs().year(year).month(startMonth).startOf('month'),
    dayjs()
      .year(year)
      .month(startMonth + 2)
      .endOf('month'),
  ]
}

export function getHalfYearRangeForYear(year: number, half: 1 | 2) {
  const startMonth = half === 1 ? 0 : 6
  return [
    dayjs().year(year).month(startMonth).startOf('month'),
    dayjs()
      .year(year)
      .month(startMonth + 5)
      .endOf('month'),
  ]
}

export function getMTD() {
  return [dayjs().startOf('month'), dayjs()]
}

export function getQTD() {
  const now = dayjs()
  const currentMonth = now.month()
  const startMonth = Math.floor(currentMonth / 3) * 3
  const startOfQuarter = now.month(startMonth).startOf('month')
  return [startOfQuarter, now]
}

export function getYTD() {
  return [dayjs().startOf('year'), dayjs()]
}

export function getRangePresets() {
  const currentYear = dayjs().year()
  const previousYear = currentYear - 1
  const currentMonth = dayjs().month()
  const currentQuarter = Math.floor(currentMonth / 3)

  // Only include quarters that have started
  const quarterLabels = ['Q1', 'Q2', 'Q3', 'Q4']
  const currentYearQuarters = quarterLabels
    .map((label, i) =>
      i <= currentQuarter
        ? {
            label: `${label} ${currentYear}`,
            value: getQuarterRangeForYear(
              currentYear,
              (i + 1) as 1 | 2 | 3 | 4
            ) as [dayjs.Dayjs, dayjs.Dayjs],
          }
        : null
    )
    .filter(Boolean) as { label: string; value: [dayjs.Dayjs, dayjs.Dayjs] }[]

  // Only include H2 if current month >= 6 (July or later)
  const currentYearHalves = [
    {
      label: `H1 ${currentYear}`,
      value: getHalfYearRangeForYear(currentYear, 1) as [
        dayjs.Dayjs,
        dayjs.Dayjs,
      ],
    },
    ...(currentMonth >= 6
      ? [
          {
            label: `H2 ${currentYear}`,
            value: getHalfYearRangeForYear(currentYear, 2) as [
              dayjs.Dayjs,
              dayjs.Dayjs,
            ],
          },
        ]
      : []),
  ]

  return [
    ...currentYearQuarters,
    ...currentYearHalves,
    { label: 'MTD', value: getMTD() as [dayjs.Dayjs, dayjs.Dayjs] },
    { label: 'QTD', value: getQTD() as [dayjs.Dayjs, dayjs.Dayjs] },
    { label: 'YTD', value: getYTD() as [dayjs.Dayjs, dayjs.Dayjs] },
    {
      label: 'LTD',
      value: [dayjs('2023-01-01'), dayjs()] as [dayjs.Dayjs, dayjs.Dayjs],
    },
    // Previous year quarters and halves (show all)
    {
      label: `Q1 ${previousYear}`,
      value: getQuarterRangeForYear(previousYear, 1) as [
        dayjs.Dayjs,
        dayjs.Dayjs,
      ],
    },
    {
      label: `Q2 ${previousYear}`,
      value: getQuarterRangeForYear(previousYear, 2) as [
        dayjs.Dayjs,
        dayjs.Dayjs,
      ],
    },
    {
      label: `Q3 ${previousYear}`,
      value: getQuarterRangeForYear(previousYear, 3) as [
        dayjs.Dayjs,
        dayjs.Dayjs,
      ],
    },
    {
      label: `Q4 ${previousYear}`,
      value: getQuarterRangeForYear(previousYear, 4) as [
        dayjs.Dayjs,
        dayjs.Dayjs,
      ],
    },
    {
      label: `H1 ${previousYear}`,
      value: getHalfYearRangeForYear(previousYear, 1) as [
        dayjs.Dayjs,
        dayjs.Dayjs,
      ],
    },
    {
      label: `H2 ${previousYear}`,
      value: getHalfYearRangeForYear(previousYear, 2) as [
        dayjs.Dayjs,
        dayjs.Dayjs,
      ],
    },
  ]
}

export function findMatchingPreset(
  startDate: dayjs.Dayjs,
  endDate: dayjs.Dayjs
): string | null {
  const presets = getRangePresets()

  for (const preset of presets) {
    const [presetStart, presetEnd] = preset.value
    if (
      startDate.isSame(presetStart, 'day') &&
      endDate.isSame(presetEnd, 'day')
    ) {
      return preset.label
    }
  }

  return null
}
